import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../utils/api';
import { format } from 'date-fns';
import { PencilIcon, CakeIcon, BriefcaseIcon, NoSymbolIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingUserDepartmentId, setEditingUserDepartmentId] = useState(null);
  const [editingUserProfileId, setEditingUserProfileId] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [profileData, setProfileData] = useState({
    birthday: '',
    hire_date: ''
  });
  const [showInactive, setShowInactive] = useState(false);

  // Fetch users, roles, and departments
  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersResponse, rolesResponse, departmentsResponse] = await Promise.all([
        api.get('/auth/users'),
        api.get('/auth/roles'),
        api.get('/departments')
      ]);
      
      setUsers(usersResponse.data.users || []);
      setRoles(rolesResponse.data.roles || []);
      setDepartments(departmentsResponse.data.departments || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again later.');
      toast.error('Failed to load users, roles, and departments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch (error) {
      return dateString;
    }
  };

  const formatCalendarDate = (dateString) => {
    if (!dateString) return 'Not set';
    
    try {
      // Handle the date string directly without creating a Date object
      // This completely avoids any timezone conversion issues
      const [year, month, day] = dateString.split('-');
      
      // Validate the components
      if (!year || !month || !day) {
        return 'Invalid date';
      }
      
      // Format manually using just the string components
      const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ];
      
      const monthIndex = parseInt(month) - 1;
      const dayNum = parseInt(day);
      
      if (monthIndex < 0 || monthIndex > 11 || dayNum < 1 || dayNum > 31) {
        return 'Invalid date';
      }
      
      return `${monthNames[monthIndex]} ${dayNum}, ${year}`;
    } catch (e) {
      return 'Invalid date';
    }
  };

  const handleChangeRole = (userId, roleId) => {
    setEditingUserId(userId);
    setSelectedRoleId(roleId || '');
  };

  const handleChangeDepartment = (userId, departmentId) => {
    setEditingUserDepartmentId(userId);
    setSelectedDepartmentId(departmentId || '');
  };

  const handleChangeProfile = (userId, user) => {
    setEditingUserProfileId(userId);
    setProfileData({
      birthday: user.birthday || '',
      hire_date: user.hire_date || ''
    });
  };

  const saveUserRole = async (userId) => {
    if (!selectedRoleId) {
      toast.error('Please select a role');
      return;
    }

    try {
      const response = await api.put(`/auth/users/${userId}/role`, { roleId: selectedRoleId });
      
      toast.success('User role updated successfully');
      setEditingUserId(null);
      
      // Refresh data to ensure we have the latest values
      fetchData();
    } catch (err) {
      console.error('Error updating user role:', err);
      toast.error('Failed to update user role');
    }
  };

  const saveUserDepartment = async (userId) => {
    try {
      const response = await api.put(`/auth/users/${userId}/department`, { 
        departmentId: selectedDepartmentId || null 
      });
      
      toast.success('User department updated successfully');
      setEditingUserDepartmentId(null);
      
      // Refresh data to ensure we have the latest values
      fetchData();
    } catch (err) {
      console.error('Error updating user department:', err);
      toast.error('Failed to update user department');
    }
  };

  const saveUserProfile = async (userId) => {
    try {
      // Only send fields that have values
      const updates = {};
      if (profileData.birthday) {
        updates.birthday = profileData.birthday;
      }
      if (profileData.hire_date) {
        updates.hire_date = profileData.hire_date;
      }

      if (Object.keys(updates).length === 0) {
        toast.error('No profile data to save');
        return;
      }

      const response = await api.put(`/auth/users/${userId}/profile`, updates);
      
      toast.success('User profile updated successfully');
      setEditingUserProfileId(null);
      setProfileData({ birthday: '', hire_date: '' });
      
      // Refresh data to ensure we have the latest values
      fetchData();
    } catch (err) {
      console.error('Error updating user profile:', err);
      toast.error('Failed to update user profile');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    const newStatus = !currentStatus;
    const action = newStatus ? 'activate' : 'deactivate';
    
    if (!window.confirm(`Are you sure you want to ${action} this user? ${!newStatus ? 'They will be excluded from calendars, leaderboards, and assignment dropdowns.' : 'They will be restored to all active user lists.'}`)) {
      return;
    }
    
    try {
      await api.put(`/auth/users/${userId}/status`, { is_active: newStatus });
      toast.success(`User ${action}d successfully`);
      fetchData();
    } catch (err) {
      console.error(`Error ${action}ing user:`, err);
      toast.error(err.response?.data?.error || `Failed to ${action} user`);
    }
  };

  // Get role name from role ID
  const getRoleNameById = (roleId) => {
    if (!roleId) return 'Not Assigned';
    const numericRoleId = parseInt(roleId, 10);
    const role = roles.find(r => parseInt(r.id, 10) === numericRoleId);
    return role ? role.name : 'Unknown';
  };

  // Get role name from user object
  const getUserRoleName = (user) => {
    // First try to get from the role object if available
    if (user.role && user.role.name) {
      return user.role.name;
    }
    
    // Fallback to getting by ID
    return getRoleNameById(user.role_type_id);
  };

  // Get department name from user object
  const getUserDepartmentName = (user) => {
    if (user.department && user.department.name) {
      return user.department.name;
    }
    return 'Not Assigned';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="w-8 h-8 border-t-2 border-b-2 border-indigo-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div>
          <h2 className="text-lg font-medium text-gray-900">System Users</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage user roles, departments, status, and calendar information
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
            <span className="ml-2 text-sm font-medium text-gray-700">Show Inactive</span>
          </label>
        </div>
      </div>

      <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Name</th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Email</th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Role</th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Department</th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Birthday</th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Hire Date</th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Google ID</th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Created At</th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Last Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {users.length === 0 ? (
              <tr>
                <td colSpan="10" className="py-4 pl-4 pr-3 text-sm text-center text-gray-500">
                  No users found
                </td>
              </tr>
            ) : (
              users.filter(user => showInactive || user.is_active !== false).map((user) => (
                <tr key={user.id} className={user.is_active === false ? 'bg-gray-50 opacity-60' : ''}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                    <div className="flex items-center">
                      {user.picture_url ? (
                        <img 
                          src={user.picture_url} 
                          alt={user.name} 
                          className={`h-8 w-8 rounded-full mr-3 ${user.is_active === false ? 'grayscale' : ''}`}
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                          <span className="text-gray-500 font-medium">
                            {user.name?.charAt(0) || '?'}
                          </span>
                        </div>
                      )}
                      {user.name}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{user.email}</td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.is_active === false
                          ? 'bg-red-100 text-red-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {user.is_active === false ? 'Inactive' : 'Active'}
                      </span>
                      <button
                        onClick={() => toggleUserStatus(user.id, user.is_active)}
                        className={`p-1 rounded-md transition-colors ${
                          user.is_active === false
                            ? 'text-green-600 hover:bg-green-50 hover:text-green-700'
                            : 'text-red-500 hover:bg-red-50 hover:text-red-700'
                        }`}
                        title={user.is_active === false ? 'Activate user' : 'Deactivate user'}
                      >
                        {user.is_active === false 
                          ? <CheckCircleIcon className="h-5 w-5" />
                          : <NoSymbolIcon className="h-5 w-5" />
                        }
                      </button>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {editingUserId === user.id ? (
                      <div className="flex items-center space-x-2">
                        <select
                          value={selectedRoleId}
                          onChange={(e) => setSelectedRoleId(e.target.value)}
                          className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="">Select a role</option>
                          {roles.map(role => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => saveUserRole(user.id)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingUserId(null)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          getUserRoleName(user) === 'admin' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {getUserRoleName(user)}
                        </span>
                        <button
                          onClick={() => handleChangeRole(user.id, user.role_type_id)}
                          className="ml-2 text-gray-500 hover:text-indigo-600"
                          title="Change role"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {editingUserDepartmentId === user.id ? (
                      <div className="flex items-center space-x-2">
                        <select
                          value={selectedDepartmentId}
                          onChange={(e) => setSelectedDepartmentId(e.target.value)}
                          className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="">No Department</option>
                          {departments.map(department => (
                            <option key={department.id} value={department.id}>
                              {department.name}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => saveUserDepartment(user.id)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingUserDepartmentId(null)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          getUserDepartmentName(user) === 'Not Assigned'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {getUserDepartmentName(user)}
                        </span>
                        <button
                          onClick={() => handleChangeDepartment(user.id, user.department?.id)}
                          className="ml-2 text-gray-500 hover:text-indigo-600"
                          title="Change department"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {editingUserProfileId === user.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="date"
                          value={profileData.birthday}
                          onChange={(e) => setProfileData({...profileData, birthday: e.target.value})}
                          className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm w-32"
                          placeholder="Birthday"
                        />
                        <button
                          onClick={() => saveUserProfile(user.id)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingUserProfileId(null);
                            setProfileData({ birthday: '', hire_date: '' });
                          }}
                          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <CakeIcon className="h-4 w-4 text-pink-500 mr-1" />
                        <span className="text-xs">
                          {formatCalendarDate(user.birthday)}
                        </span>
                        <button
                          onClick={() => handleChangeProfile(user.id, user)}
                          className="ml-2 text-gray-500 hover:text-indigo-600"
                          title="Edit calendar info"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {editingUserProfileId === user.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="date"
                          value={profileData.hire_date}
                          onChange={(e) => setProfileData({...profileData, hire_date: e.target.value})}
                          className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm w-32"
                          placeholder="Hire Date"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <BriefcaseIcon className="h-4 w-4 text-blue-500 mr-1" />
                        <span className="text-xs">
                          {formatCalendarDate(user.hire_date)}
                        </span>
                        <button
                          onClick={() => handleChangeProfile(user.id, user)}
                          className="ml-2 text-gray-500 hover:text-indigo-600"
                          title="Edit calendar info"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {user.google_id ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Connected
                      </span>
                    ) : 'Not connected'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {formatDate(user.updated_at)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Information Card */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Calendar Information Management
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Birthday and hire date information is used for company calendar events</li>
                <li>Birthdays appear on the company calendar for team celebrations</li>
                <li>Work anniversaries are calculated automatically from hire dates</li>
                <li>Users can also update their own calendar information from their profile page</li>
                <li>This information is visible to all team members on the company calendar</li>
                <li><strong>Deactivating a user</strong> removes them from calendars, leaderboards, and assignment lists while preserving their data</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users; 